import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatDate } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

const STATUS = {
  presente: { label: "Presente", cls: "bg-emerald-50 text-emerald-800 border-emerald-300", mark: "✓" },
  pendiente: { label: "Pendiente", cls: "bg-gray-100 text-gray-800 border-gray-300", mark: "○" },
  requiere_revision: { label: "Requiere revisión", cls: "bg-amber-50 text-amber-900 border-amber-300", mark: "!" },
  no_aplica: { label: "No aplica", cls: "bg-white text-gray-500 border-gray-300", mark: "—" },
};

function linkFor(item, caseId) {
  if (item.link === "edit_draft") return { to: `/cases/${caseId}/edit-draft`, label: "Ir al borrador" };
  if (item.link === "nivel2_request") return { to: `/cases/${caseId}/nivel2-request`, label: "Ir al formulario del Kit" };
  return null;
}

function Item({ item, caseId, onSave }) {
  const [note, setNote] = useState(item.note || "");
  useEffect(() => setNote(item.note || ""), [item.note]);
  const l = linkFor(item, caseId);
  const st = STATUS[item.status];
  return (
    <li className="py-4 border-t border-gray-100 break-inside-avoid" data-testid={`checklist-item-${item.id}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-3">
        <span className={`print:inline hidden w-5 font-mono`}>{st.mark}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900 font-medium">{item.label}</p>
          <p className="text-xs text-gray-600 mt-0.5">{item.explanation}</p>
          {item.how_to_resolve && <p className="text-xs text-gray-700 mt-1"><strong>Cómo resolverlo:</strong> {item.how_to_resolve}</p>}
          {l && (
            <Link to={l.to} className="print:hidden inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1" data-testid={`checklist-link-${item.id}`}>
              {l.label} <ArrowRight className="w-3 h-3" />
            </Link>
          )}
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => note !== (item.note || "") && onSave(item, { status: item.edited ? item.status : null, note })}
            placeholder="Nota (opcional)"
            className="mt-2 min-h-[38px] text-xs rounded-sm print:hidden"
            data-testid={`checklist-note-${item.id}`}
          />
          {item.note && <p className="hidden print:block text-xs italic mt-1">Nota: {item.note}</p>}
        </div>
        <div className="md:w-44 shrink-0">
          <select
            value={item.status}
            onChange={(e) => onSave(item, { status: e.target.value, note })}
            className={`w-full border text-xs px-2 py-1.5 rounded-sm print:hidden ${st.cls}`}
            data-testid={`checklist-status-${item.id}`}
          >
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <span className={`hidden print:inline-block border px-2 py-0.5 text-[10px] ${st.cls}`}>{st.label}</span>
          {item.edited && <p className="text-[10px] text-gray-400 mt-1 print:hidden">Editado por ti · sugerido: {STATUS[item.auto_status].label}</p>}
        </div>
      </div>
    </li>
  );
}

function Summary({ summary }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-8" data-testid="checklist-summary">
      {Object.entries(STATUS).map(([k, v]) => (
        <div key={k} className={`border p-3 ${v.cls}`}>
          <p className="text-2xl font-serif" data-testid={`checklist-count-${k}`}>{summary?.[k] ?? 0}</p>
          <p className="text-[10px] uppercase tracking-widest">{v.label}</p>
        </div>
      ))}
    </div>
  );
}

export default function CaseChecklistPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [caseDoc, setCaseDoc] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, ck] = await Promise.all([api.get(`/cases/${id}`), api.get(`/cases/${id}/checklist`)]);
      setCaseDoc(c.data);
      setData(ck.data);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "No se pudo cargar el checklist");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const onSave = async (item, body) => {
    try {
      const { data: d } = await api.put(`/cases/${id}/checklist/items/${item.id}`, body);
      setData(d);
      toast.success("Checklist guardado");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudo guardar");
    }
  };

  return (
    <div className="container-prose py-12 print:py-0" data-testid="case-checklist-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Link to={`/cases/${id}`} className="text-sm text-gray-500 hover:text-primary" data-testid="checklist-back">← Volver al caso</Link>
          <Button onClick={() => window.print()} variant="outline" className="rounded-sm h-10" data-testid="checklist-print-btn">
            <Printer className="w-4 h-4 mr-2" />Imprimir / Guardar PDF
          </Button>
        </div>
        {error && <div className="border border-destructive/30 bg-destructive/5 text-destructive p-4 text-sm" data-testid="checklist-error">{error}</div>}
        {data && caseDoc && (
          <>
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">Checklist precontractual</p>
            <h1 className="font-serif text-3xl text-gray-900 mb-2" data-testid="checklist-title">{caseDoc.title}</h1>
            <p className="text-xs text-gray-500 mb-6">
              {caseDoc.entity_name && <>{caseDoc.entity_name} · </>}Borrador v{data.intake_version || 1}
              {data.diagnosis_version ? ` · Diagnóstico v${data.diagnosis_version}` : " · Sin diagnóstico aún"}
              {data.updated_at && ` · Última edición ${formatDate(data.updated_at)}`}
            </p>
            <Summary summary={data.summary} />
            {data.phases.map((p) => (
              <section key={p.key} className="mb-8 border border-gray-200 bg-white p-6 print:border-0 print:p-0" data-testid={`checklist-phase-${p.key}`}>
                <h2 className="font-serif text-lg text-gray-900">{p.label}</h2>
                {p.items.length === 0
                  ? <p className="text-xs text-gray-500 mt-2">Sin ítems en esta fase.</p>
                  : <ul>{p.items.map((it) => <Item key={it.id} item={it} caseId={id} onSave={onSave} />)}</ul>}
              </section>
            ))}
            <p className="text-[11px] text-gray-400">
              Checklist de apoyo generado con reglas explícitas sobre tus datos y el diagnóstico. No sustituye la revisión jurídica de la entidad.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
